import { PropsWithChildren, useCallback, useEffect, useRef } from "react";

import { ArrowLeftIcon, Cross1Icon, SizeIcon } from "@radix-ui/react-icons";
import { Container, Flex, Heading } from "@radix-ui/themes";
import { styled } from "@stitches/react";

import { IconButton } from "../../common/components/IconButton";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useDrawerContext } from "../../state/DrawerContext";

export type Props = PropsWithChildren<{
  id: string;
  isPrevious?: boolean;
  previousAvailable?: boolean;
  restorePreviousDrawer?: () => void;
  onClose?: () => void;
  onOutsideClick?: () => void;
  title: React.ReactNode;
  onBack?: () => void;
  onExpand?: () => void;
}>;

const DrawerContainer = styled(Container, {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  background: "var(--gray-1)",
  boxShadow: "0px 5px 12px 4px rgba(0, 0, 0, 0.12)",
  overflow: "auto",
  zIndex: 5,
  transition: "transform 0.1s cubic-bezier(0.4, 0, 1, 1)",
  transformBox: "content-box",
  transform: "translateX(100%)",

  "> .rt-ContainerInner": {
    width: "100vw",
  },
});

export const Drawer = (props: Props) => {
  const { isOpen, isPrevious, previousAvailable, restorePreviousDrawer } =
    useDrawerContext();

  if (isOpen(props.id)) {
    return (
      <DrawerImpl
        {...props}
        previousAvailable={previousAvailable}
        restorePreviousDrawer={restorePreviousDrawer}
        isPrevious={isPrevious(props.id)}
      />
    );
  }

  return null;
};

const DrawerImpl = ({
  id,
  isPrevious,
  previousAvailable,
  restorePreviousDrawer,
  children,
  onOutsideClick,
  onBack,
  title,
  onClose,
  onExpand,
}: Props) => {
  const { closeDrawer, isOpen } = useDrawerContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const handleClose = useCallback(() => {
    closeDrawer();
    onClose?.();
  }, [closeDrawer, onClose]);

  const handleOutsideClick = useCallback(() => {
    onOutsideClick?.();
  }, [onOutsideClick]);

  useOutsideClick({ ref: containerRef, handler: handleOutsideClick });

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.transform = isOpen(id)
        ? "translateX(0%)"
        : "translateX(100%)";
    }
  }, [id, isOpen]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else if (previousAvailable) {
      restorePreviousDrawer?.();
    }
  }, [onBack, previousAvailable, restorePreviousDrawer]);

  const backAvailable = onBack || (previousAvailable && !isPrevious);
  const titleIsString = typeof title === "string";

  return (
    <DrawerContainer
      ref={containerRef}
      size={{
        initial: "1",
        xs: "1",
        sm: "1",
        md: "1",
        lg: "2",
        xl: "3",
      }}
    >
      <Container size="1">
        <Flex direction={"column"} gap={"4"} p="4">
          <Flex direction={"row"} justify={"between"} align={"center"} gap="7">
            <Flex gap="2" align="center" style={{ alignSelf: "flex-start" }}>
              {backAvailable && (
                <IconButton size="1" onClick={handleBack} variant="ghost">
                  <ArrowLeftIcon />
                </IconButton>
              )}
              {titleIsString ? <Heading size="5">{title}</Heading> : title}
            </Flex>
            <Flex gap="2" style={{ alignSelf: "flex-start" }}>
              {onExpand && (
                <IconButton onClick={onExpand} variant="ghost" color="gray">
                  <SizeIcon />
                </IconButton>
              )}
              <IconButton onClick={handleClose} variant="ghost" color="gray">
                <Cross1Icon />
              </IconButton>
            </Flex>
          </Flex>
          {children}
        </Flex>
      </Container>
    </DrawerContainer>
  );
};
